import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../../../components/Layout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Button } from '../../../components/ui/button';
import { Users, Settings, TabletSmartphone, ScrollText, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import { UserManagement } from '../components/UserManagement';
import { SystemSettings } from '../components/SystemSettings';
import { mockUsers } from '../data/mockUsers';
import { UserAccessIndex } from '../components/UserAccess/Index';
import { DeviceManagement } from '../components/DeviceManagement';
import { AdminAuditTrail } from '../components/AuditTrail/AdminAuditTrail';
import { AdminEmailConfiguration } from '../components/EmailConfiguration/AdminEmailConfiguration';
import { useUserPermissionsAfterLogin } from '../hooks/useUserPermissionsAfterLogin';
import { logActivityPageView } from '../../audit/activityAudit';

interface AdminPageProps {
  currentUser: any; // Replace with proper type
}

const ADMIN_TABS_CONFIG = {
  users: {
    label: 'User Management',
    icon: Users,
    content: (props: any) => <UserManagement {...props} />,
    name: 'manage_users',
  },
  emailConfig: {
    label: 'Email Configuration',
    icon: Mail,
    content: () => <AdminEmailConfiguration canEdit />,
    name: 'manage_system',
  },
  settings: {
    label: 'System Settings',
    icon: Settings,
    content: (props: any) => <SystemSettings {...props} />,
    name: 'manage_system',
  },
  
  userAccessSettings: {
    label: 'User Access Settings',
    icon: Settings,
    content: (props: any) => <UserAccessIndex {...props} />,
    name: 'manage_user_access',
  },
  auditTrail: {
    label: 'Audit Trail',
    icon: ScrollText,
    content: () => <AdminAuditTrail />,
    name: 'view_audit_logs',
  },
} as const;

const AdminPage: React.FC = () => {
  const currentUser = mockUsers[0];
  const [activeTab, setActiveTab] = useState('users');
  const { hasPermission } = useUserPermissionsAfterLogin();

  const visibleTabs = useMemo(
    () =>
      Object.entries(ADMIN_TABS_CONFIG)
        .filter(([, { name }]) => hasPermission(name))
        .map(([key]) => key),
    [hasPermission]
  );

  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollTabsLeft, setCanScrollTabsLeft] = useState(false);
  const [canScrollTabsRight, setCanScrollTabsRight] = useState(false);

  const updateTabScrollButtons = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollTabsLeft(scrollLeft > 1);
    setCanScrollTabsRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    updateTabScrollButtons();
    const onScroll = () => updateTabScrollButtons();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateTabScrollButtons());
    ro.observe(el);
    const tabList = el.firstElementChild;
    if (tabList) ro.observe(tabList);
    window.addEventListener('resize', updateTabScrollButtons);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.removeEventListener('resize', updateTabScrollButtons);
    };
  }, [visibleTabs, updateTabScrollButtons]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => updateTabScrollButtons());
    return () => cancelAnimationFrame(frame);
  }, [visibleTabs, activeTab, updateTabScrollButtons]);

  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const activeTrigger = container.querySelector<HTMLElement>('[data-state="active"]');
    if (activeTrigger) {
      const { offsetLeft, offsetWidth } = activeTrigger;
      const { scrollLeft, clientWidth } = container;
      const isFullyVisible =
        offsetLeft >= scrollLeft &&
        offsetLeft + offsetWidth <= scrollLeft + clientWidth;

      if (!isFullyVisible) {
        activeTrigger.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
      updateTabScrollButtons();
    }
  }, [activeTab, visibleTabs.length, updateTabScrollButtons]);

  const handleAdminTabChange = (value: string) => {
    const label = ADMIN_TABS_CONFIG[value as keyof typeof ADMIN_TABS_CONFIG]?.label ?? value;
    logActivityPageView(`Admin — ${label}`, { tab: value });
    setActiveTab(value);
  };

  const handleScrollTabsLeft = () => {
    tabsContainerRef.current?.scrollBy({ left: -160, behavior: 'smooth' });
  };

  const handleScrollTabsRight = () => {
    tabsContainerRef.current?.scrollBy({ left: 160, behavior: 'smooth' });
  };

  return (
    <Layout>
      <div className="min-w-0 w-full">
        <div className="max-w-[2000px] mx-auto min-w-0">
          <div className="bg-white rounded-lg shadow-sm min-h-0">
            <Tabs
              value={activeTab}
              onValueChange={handleAdminTabChange}
              className="w-full min-w-0 overflow-hidden"
            >
              <div className="sticky top-0 z-10 rounded-t-lg border-b border-[#E2E5EA] bg-white">
                <div className="flex items-center gap-1 bg-transparent px-2 pt-2 pb-0 md:px-4 md:pt-3">
                  {canScrollTabsLeft && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mb-2 shrink-0 text-heritage-grey hover:text-blueprint-blue"
                      onClick={handleScrollTabsLeft}
                      aria-label="Scroll tabs left"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                  )}
                  <div
                    ref={tabsContainerRef}
                    className="flex-1 min-w-0 overflow-x-auto overflow-y-hidden whitespace-nowrap [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                  >
                    <TabsList className="h-auto w-max justify-start gap-4 bg-transparent p-0 rounded-none border-0 md:gap-6 lg:gap-8">
                      {visibleTabs.map((key) => {
                        const { label, icon: Icon } =
                          ADMIN_TABS_CONFIG[key as keyof typeof ADMIN_TABS_CONFIG];
                        return (
                          <TabsTrigger
                            key={key}
                            value={key}
                            className="relative inline-flex flex-shrink-0 items-center gap-1.5 rounded-none border-b-[3px] border-transparent px-0.5 pb-3 pt-1.5 text-sm font-medium text-heritage-grey hover:text-blueprint-blue whitespace-nowrap data-[state=active]:border-hero-red data-[state=active]:text-hero-red focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                          >
                            <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                            {label}
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </div>
                  {canScrollTabsRight && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mb-2 shrink-0 text-heritage-grey hover:text-blueprint-blue"
                      onClick={handleScrollTabsRight}
                      aria-label="Scroll tabs right"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  )}
                </div>
              </div>

              {visibleTabs.map((key) => {
                const Content = ADMIN_TABS_CONFIG[key as keyof typeof ADMIN_TABS_CONFIG].content;
                return (
                  <TabsContent key={key} value={key} className="mt-0 min-w-0">
                    <div className="min-w-0 px-2 py-3 sm:px-4 sm:py-4">
                      <Content currentUser={currentUser} />
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminPage;
